import * as React from "react";

import Footer from "../footer";
import { Text } from "@uikit";
import TopBar from "../topBar";
import Navbar from "../navbar";
import { Breadcrumb } from "@kalabo/golfsavers-common";
import { Image } from "@kalabo/golfsavers-common";
import BreadcrumbComponent from "../common/breadcrumb";
import PersonPicker from "../course/personPicker";
import DatePicker from "../course/datePicker";
import Currencies from "@components/navbar/currencies";
import LoginSignUp from "@components/navbar/loginSignup";
import FindBooking from "@components/navbar/findBooking";
import { useApp } from "@store/app";
import dayjs from "dayjs";
import { useCheckout } from "@store/checkout";

interface Handles {
  scrollToTop: () => void;
}

interface IProps {
  pageTitle?: string;
  bannerImage?: Image;
  bannerTitle?: string;
  bannerSubTitle?: string;
  subTitle?: string;
  barTitle?: boolean;
  fullWidth?: boolean;
  focusedMode?: boolean;
  hideFooter?: boolean;
  simpleFooter?: boolean;
  searchMode?: boolean;
  fixedFooter?: boolean;
  greyBody?: boolean;
  hideHeader?: boolean;
  hideTopBar?: boolean;
  hidePadding?: boolean;
  hideBreadcrumb?: boolean;
  hideNavigation?: boolean;
  hideMobileNavigation?: boolean;
  breadcrumb?: Breadcrumb[];
  breadcrumbHideMobile?: boolean;
  keywords?: string;
  backgroundUrl?: string;
  description?: string;
  canonical?: string;
  children: React.ReactNode;
  barContent?: React.ReactNode;
  region_id?: string;
  className?: string;
}

const Layout = React.forwardRef<Handles, IProps>(
  (
    {
      pageTitle,
      subTitle,
      bannerImage,
      bannerTitle,
      bannerSubTitle,
      hideTopBar = false,
      focusedMode = false,
      barTitle = false,
      fullWidth = false,
      hidePadding = false,
      greyBody = false,
      hideFooter = false,
      hideHeader = false,
      simpleFooter = false,
      hideBreadcrumb = false,
      hideNavigation = false,
      hideMobileNavigation = false,
      breadcrumbHideMobile = false,
      searchMode = false,
      backgroundUrl,
      region_id,
      breadcrumb = [],
      keywords = "",
      description = "",
      canonical = "",
      barContent,
      children,
    },
    ref
  ) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [hasScrollbar, setHasScrollbar] = React.useState(false);
    const date = useApp(($) => $.date);
    const setDate = useApp(($) => $.setDate);
    const cart = useCheckout(($) => $.cart);
    const removeFromCart = useCheckout(($) => $.removeFromCart);

    React.useImperativeHandle(ref, () => {
      return {
        scrollToTop: () => {
          contentRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        },
      };
    });

    React.useEffect(() => {
      const dataCleanup = async () => {
        // First check if the date is valid and if not, set it to today + 2 days
        if (dayjs(date).isBefore(dayjs().startOf("day"))) {
          setDate(dayjs().add(2, "day").format("YYYY-MM-DD"));
        }

        // check there is no expired items in the cart

        // get a list of all items in the cart that are expired
        const expiredItems = cart.filter((item: any) => {
          let date_of_play = item.golf.where_when_who.date;
          return dayjs(date_of_play).isBefore(dayjs().startOf("day"));
        });

        for (let i = 0; i < expiredItems.length; i++) {
          const item = expiredItems[i];
          await removeFromCart(item.id);
        }
      };

      dataCleanup();
    }, []);

    React.useEffect(() => {
      const checkForScrollbar = () => {
        if (contentRef.current) {
          const hasVerticalScrollbar =
            contentRef.current.scrollHeight > contentRef.current.clientHeight;
          setHasScrollbar(hasVerticalScrollbar);
        }
      };

      // Initial check
      checkForScrollbar();

      // Setup resize observer to check when content changes
      const resizeObserver = new ResizeObserver(checkForScrollbar);
      if (contentRef.current) {
        resizeObserver.observe(contentRef.current);
      }

      return () => resizeObserver.disconnect();
    }, []);

    const showPeoplePicker = useApp(($) => $.showPeoplePicker);
    const showNightPicker = useApp(($) => $.showNightPicker);
    const showPackageDatePicker = useApp(($) => $.showPackageDatePicker);
    const showDatePicker = useApp(($) => $.showDatePicker);
    const showCurrencyPicker = useApp(($) => $.showCurrencyPicker);
    const showFindBookingModal = useApp(($) => $.showFindBookingModal);
    const showLoginModal = useApp(($) => $.showLoginModal);
    const hideModals = useApp(($) => $.hideModals);

    const offsetHeight = React.useMemo(() => {
      if (hideHeader) {
        return "h-[calc(100dvh-44px)]  sm:h-[calc(100dvh-44px)]  lg:h-[calc(100dvh-44px)]";
      }
      if (!hideBreadcrumb) {
        return "h-[calc(100dvh-153px)] sm:h-[calc(100dvh-231px)] lg:h-[calc(100dvh-251px)]";
      }

      if (hideBreadcrumb && hideTopBar && !hideFooter) {
        return "h-[calc(100dvh-61px)]  sm:h-[calc(100dvh-85px)] md:h-[calc(100dvh-110px)] lg:h-[calc(100dvh-119px)]";
      }

      if (hideBreadcrumb && hideTopBar && hideFooter) {
        return "h-[calc(100dvh-79px)]  sm:h-[calc(100dvh-79px)] md:h-[calc(100dvh-145px)] lg:h-[calc(100dvh-153px)]";
      }

      return "h-[calc(100dvh-73px)]  sm:h-[calc(100dvh-117px)]";
    }, []);

    return (
      <>
        <div className={`min-h-full ${greyBody && "bg-grey"}`}>
          {!hideTopBar && <TopBar />}

          {hideHeader ? null : (
            <>
              <div
                className={`${
                  hideMobileNavigation ? "hidden md:block" : "block"
                } `}
              >
                <Navbar searchMode={searchMode} hideNav={hideNavigation} />
              </div>

              {!hideBreadcrumb && breadcrumb.length > 0 && (
                <div
                  className={`${breadcrumbHideMobile && "hidden md:block"} ${
                    bannerImage ? "" : ""
                  }`}
                >
                  <BreadcrumbComponent
                    breadcrumb={breadcrumb}
                    region_id={region_id}
                  />
                </div>
              )}
            </>
          )}
          {bannerImage && (
            <div
              className="mb-5 sm:mb-10 lg:mb-10 h-[240px] z-0 relative sm:h-[410px] flex items-center justify-center"
              style={{
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundImage: `url(${process.env.GATSBY_IMAGE_PREFIX}${bannerImage?.url})`,
              }}
            >
              {bannerTitle && bannerSubTitle && (
                <div className="flex gap-3 flex-col items-center justify-center">
                  <div className="flex gap-3 sm:gap-6 mt-12 ">
                    {bannerTitle.split(" ").map((word, wIdx) => (
                      <Text
                        key={wIdx}
                        size={35}
                        color="white"
                        textShadow
                        className="!text-[60px] sm:!text-[108px] !font-gloss  z-10 "
                      >
                        {word}
                      </Text>
                    ))}
                  </div>{" "}
                  <Text
                    size={21}
                    sizeTablet={35}
                    sizeDesktop={35}
                    color="white"
                    textShadow
                    weight="bold"
                    className="uppercase z-40"
                  >
                    {bannerSubTitle}
                  </Text>
                </div>
              )}
            </div>
          )}
          <main
            className={`${focusedMode && offsetHeight} ${
              !hidePadding ? "sm:pt-5 lg:pt-10" : ""
            }  `}
          >
            {barTitle && (
              <div
                style={{
                  backgroundImage: `url(${backgroundUrl})`,
                }}
                className="bg-blue md:flex md:items-center xl:mt-0 flex flex-col h-[340px] relative bg-cover bg-center"
              >
                <div className={`relative z-[2] p-6 h-full `}>
                  {barContent ? (
                    barContent
                  ) : (
                    <>
                      <Text size={32} color="white">
                        {pageTitle}
                      </Text>
                      <Text size={20} color="white">
                        {subTitle}
                      </Text>
                    </>
                  )}
                </div>
                <div className="absolute z-[1] h-full w-full bg-black/50"></div>
              </div>
            )}
            <div
              ref={contentRef}
              style={
                hasScrollbar
                  ? {
                      marginLeft: "20px",
                    }
                  : {}
              }
              className={`${focusedMode && "h-full overflow-auto"} ${
                fullWidth
                  ? "max-w-full flex flex-col "
                  : "lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0"
              } min-h-100     `}
            >
              {children}
            </div>
          </main>
          {!hideFooter && <Footer focusedMode={focusedMode || simpleFooter} />}
          {(showPeoplePicker || showNightPicker) && <PersonPicker />}
          {(showDatePicker || showPackageDatePicker) && <DatePicker />}

          <Currencies visible={showCurrencyPicker} />
          <FindBooking visible={showFindBookingModal} />

          <LoginSignUp
            visible={showLoginModal}
            defaultMode="home"
            onSignIn={() => {
              hideModals();
            }}
          />
        </div>
      </>
    );
  }
);
export default Layout;
